import { Typography } from '@euler/app/components/typography';
import { useCurrentUserDangerously } from '@euler/app/flows/auth/context';
import { GeneralNavHeader } from '@euler/app/tv/components/GeneralNavHeader';
import { kScreenContentPadding } from '@euler/app/tv/functions/constants';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { Img } from '@euler/components/image/AdvancedImage';
import { HStack } from '@euler/components/layout/Stack';
import { oemConfig } from '@euler/config/oem';
import {
  kPrivacyPolicyUrl,
  kTermsOfServiceUrl,
} from '@euler/functions/constants';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import * as WebBrowser from 'expo-web-browser';
import { memo, useCallback } from 'react';
import {
  ScrollView,
  StyleProp,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const kLogoSize = 360;
const kLogoPadding = 20;
const kDefaultLogoUrl =
  'https://img.zhichetech.com/files/agent/logos/f8e50bb90c97b6b70f1372030c74d8b7.png';
const kDefaultDescription = oemConfig.description;

export const AboutScreen = wrapNavigatorScreen(
  () => {
    const { rpx } = useResponsiveContext();
    const insets = useSafeAreaInsets();
    const { agent } = useCurrentUserDangerously();
    const {
      about: { nameTextColor, descriptionTextColor, copyrightTextColor },
    } = usePresentationTheme();
    const website = agent == null ? oemConfig.website : agent.website;
    const onPressWebsite = useCallback(() => {
      if (website == null) return;
      WebBrowser.openBrowserAsync(website).catch(() => null);
    }, [website]);

    return (
      <View
        css={`
          flex: 1;
          padding-bottom: ${insets.bottom + rpx(40)}px;
        `}
      >
        <GeneralNavHeader title="关于我们" />
        <HStack
          justifyContent="flex-start"
          alignItems="stretch"
          css={`
            flex: 1;
            margin-left: ${rpx(kScreenContentPadding)}px;
            margin-right: ${rpx(kScreenContentPadding)}px;
          `}
        >
          <View
            css={`
              align-items: center;
            `}
          >
            <Img
              uri={agent?.logoUrl ?? kDefaultLogoUrl}
              css={`
                width: ${rpx(kLogoSize + 2 * kLogoPadding)}px;
                height: ${rpx(kLogoSize + 2 * kLogoPadding)}px;
                border-radius: ${rpx(80)}px;
              `}
            />
          </View>
          <View
            css={`
              margin-left: ${rpx(40)}px;
              flex: 1;
            `}
          >
            <ScrollView
              contentContainerStyle={{
                flexGrow: 1,
              }}
              css={`
                flex: 1;
              `}
            >
              <Typography.Label
                bold
                size={rpx(48)}
                lineHeight={rpx(64)}
                color={nameTextColor}
              >
                {agent?.name ?? oemConfig.fullName}
              </Typography.Label>

              {agent == null || agent.remark ? (
                <Typography.Label
                  thin
                  size={rpx(24)}
                  lineHeight={rpx(36)}
                  color={descriptionTextColor}
                  css={`
                    margin-top: ${rpx(20)}px;
                  `}
                >
                  {agent == null ? kDefaultDescription : agent.remark ?? ''}
                </Typography.Label>
              ) : null}

              <Cell
                label="联系我们"
                text={agent == null ? '028-86751230' : agent.contactPhone ?? ''}
                css={`
                  margin-top: ${rpx(40)}px;
                `}
              />
              <Cell
                label="联系地址"
                text={
                  agent == null
                    ? '中国（四川）自由贸易试验区成都高新区天府五街200号4栋8层802室'
                    : agent.address ?? ''
                }
              />
              {website ? (
                <Cell
                  label="官方网站"
                  text={website}
                  onPress={onPressWebsite}
                />
              ) : null}
            </ScrollView>
            <View>
              <Typography.Label
                light
                size={rpx(20)}
                color={copyrightTextColor}
                lineHeight={rpx(36)}
                css={`
                  margin-top: ${rpx(40)}px;
                `}
              >
                {`Powed by zhichetech.com, © 2019-2022. All rights reserved. `}
              </Typography.Label>
              <HStack
                justifyContent="flex-start"
                css={`
                  margin-top: ${rpx(10)}px;
                `}
              >
                <Link
                  text="隐私政策"
                  url={oemConfig.privacyPolicyUrl ?? kPrivacyPolicyUrl}
                  secondary
                />
                <Link
                  text="服务条款"
                  url={oemConfig.termsOfServiceUrl ?? kTermsOfServiceUrl}
                  secondary
                  css={`
                    margin-left: ${rpx(24)}px;
                  `}
                />
              </HStack>
            </View>
          </View>
        </HStack>
      </View>
    );
  },
  {
    headerShown: false,
  },
);

const Cell = memo(
  ({
    label,
    text,
    style,
    onPress,
  }: {
    label: string;
    text: string;
    style?: StyleProp<ViewStyle>;
    onPress?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const {
      about: {
        infoLabelTextColor,
        infoTextColor,
        separatorLineColor,
        linkTextColor,
      },
    } = usePresentationTheme();
    return (
      <View
        css={`
          padding: ${rpx(16)}px 0;
          border-bottom-width: 1px;
          border-bottom-color: ${separatorLineColor};
        `}
        style={style}
      >
        <Typography.Label
          size={rpx(18)}
          color={infoLabelTextColor}
          lineHeight={rpx(36)}
        >
          {label}
        </Typography.Label>
        {onPress != null ? (
          <TouchableOpacity onPress={onPress}>
            <Typography.Label
              size={rpx(28)}
              color={linkTextColor}
              lineHeight={rpx(40)}
            >
              {text}
            </Typography.Label>
          </TouchableOpacity>
        ) : (
          <Typography.Label
            size={rpx(28)}
            color={infoTextColor}
            lineHeight={rpx(40)}
          >
            {text}
          </Typography.Label>
        )}
      </View>
    );
  },
);

const Link = memo(
  ({
    text,
    url,
    secondary,
    style,
  }: {
    text: string;
    url: string;
    secondary?: boolean;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    const {
      about: { linkTextColor, secondaryLinkTextColor },
    } = usePresentationTheme();

    const onPress = useCallback(() => {
      WebBrowser.openBrowserAsync(url).catch(() => null);
    }, [url]);

    return (
      <TouchableOpacity onPress={onPress} style={style}>
        <Typography.Label
          size={rpx(20)}
          color={secondary ? secondaryLinkTextColor : linkTextColor}
          lineHeight={rpx(36)}
        >
          {text}
        </Typography.Label>
      </TouchableOpacity>
    );
  },
);
